import { formatDate, getCurrentUser } from '@/utils/common.util';
import { Modal } from 'carbon-components-react';
import React from 'react';
import './student-transcript.styles.scss';
import { RenderIf } from '@/components/RenderIf';
import CustomButton from '@/components/custom-button/custom-btn.component';
import { Printer } from 'react-feather';
import { PrintView } from '@/components/PrintView/print-view.component';
import SignatureForm from '@/components/signature/SignatureForm';

export default function StudentTranscriptModal({
  isOpen,
  closeModal,
  student,
  transcriptData,
}) {
  const school = getCurrentUser()?.school;
  const data = transcriptData || {};
  const studentInfo = data.student || student || {};
  const coverage = data.coverage || {};
  const academicYears = data.academicYears || [];
  const gradingScales = data.gradingScales || [];

  const getScaleLabel = (gradingScaleId) => {
    if (gradingScales.length <= 1) return '';
    const scale = gradingScales.find(
      (s) => s.gradingScaleId === gradingScaleId,
    );
    return scale ? `Graded on: ${scale.description}` : '';
  };

  const formatTermDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderGradingScales = () => {
    if (!gradingScales || gradingScales.length === 0) return null;

    if (gradingScales.length === 1) {
      const scale = gradingScales[0];
      return (
        <div className="mt-70">
          <div className="text-14 font-bold mb-10">
            Grading Scale Interpretation
          </div>
          <table className="bx--data-table table-transparent">
            <thead>
              <tr>
                <th>Score Range</th>
                <th>Grade</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {scale.items?.map((item, idx) => (
                <tr key={idx}>
                  <td>
                    {item.minimum} - {item.maximum}
                  </td>
                  <td>
                    <span className="font-bold">{item.grade}</span>
                  </td>
                  <td>{item.remarks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div className="mt-70">
        <div className="text-14 font-bold mb-10">
          Grading Scale Interpretation
        </div>
        {gradingScales.map((scale) => (
          <div key={scale.gradingScaleId} className="mb-30">
            <div className="text-14 font-bold mb-5">
              {scale.description}
              <span className="text-grey-dark font-normal">
                {' '}
                — Applies to: {scale.appliesToClasses?.join(', ')}
              </span>
            </div>
            <table className="bx--data-table table-transparent">
              <thead>
                <tr>
                  <th>Score Range</th>
                  <th>Grade</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {scale.items?.map((item, idx) => (
                  <tr key={idx}>
                    <td>
                      {item.minimum} - {item.maximum}
                    </td>
                    <td>
                      <span className="font-bold">{item.grade}</span>
                    </td>
                    <td>{item.remarks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  const renderTerm = (term) => {
    const hasSubjects = term.subjects && term.subjects.length > 0;

    return (
      <div key={term.gradingId} className="mb-20">
        <div className="d-flex justify-between align-items-center mb-10">
          <div className="text-14 text-grey-dark font-bold">{term.term}</div>
          <div className="text-12 text-grey-dark">
            {formatTermDate(term.termStartDate)} —{' '}
            {formatTermDate(term.termEndDate)}
          </div>
        </div>

        <RenderIf
          condition={
            gradingScales.length > 1 && getScaleLabel(term.gradingScaleId)
          }
        >
          <div className="text-12 text-primary mb-10">
            {getScaleLabel(term.gradingScaleId)}
          </div>
        </RenderIf>

        <table className="bx--data-table table-transparent">
          <thead>
            <tr>
              <th>Subject</th>
              <th>Total Score</th>
              <th>Grade</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {hasSubjects ? (
              term.subjects.map((subject) => (
                <tr key={subject.subjectId}>
                  <td>{subject.subject}</td>
                  <td>{subject.totalScore?.toFixed(2)}</td>
                  <td>
                    <span className="font-bold">
                      {subject.grade ? subject.grade : '-'}
                    </span>
                  </td>
                  <td>
                    <span className="font-bold">{subject.remarks}</span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center text-grey-dark">
                  No subject marks recorded
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* <div className="d-flex justify-between mt-10 text-14">
          <RenderIf condition={term.totalGradeAggregate != null}>
            <div>
              <span className="text-grey-dark">Total Grade Aggregate: </span>
              <span className="font-bold">{term.totalGradeAggregate}</span>
            </div>
          </RenderIf>
          <RenderIf condition={term.position}>
            <div>
              <span className="text-grey-dark">Position: </span>
              <span className="font-bold">{term.position}</span>
              <span className="text-grey-dark">
                {' '}
                out of {term.numberOnRoll}
              </span>
            </div>
          </RenderIf>
        </div>

        <RenderIf condition={term.teacherRemarks || term.headMasterRemark}>
          <div className="mt-10 text-14">
            {term.teacherRemarks && (
              <p>
                <span className="text-grey-dark">Teacher's Remarks: </span>
                {term.teacherRemarks}
              </p>
            )}
            {term.headMasterRemark && (
              <p>
                <span className="text-grey-dark">Head Teacher's Remarks: </span>
                {term.headMasterRemark}
              </p>
            )}
          </div>
        </RenderIf>

        <RenderIf condition={term.totalSchoolDays != null && term.studentAttendance != null}>
          <div className="mt-10 text-14 text-grey-dark">
            Attendance: {term.studentAttendance} / {term.totalSchoolDays} days
          </div>
        </RenderIf> */}
      </div>
    );
  };

  return (
    <PrintView>
      <Modal
        id="student-transcript-modal"
        className="print-modal big-modal student-transcript"
        passiveModal={true}
        open={isOpen}
        primaryButtonDisabled={false}
      >
        <div className="modal-inner">
          <div className="inner-content">
            <div className="mr-20">
              <div className="mt-5 mb-10 d-flex">
                <div
                  className="backgroundImage"
                  style={{
                    backgroundImage: `url(${school.logoUrl})`,
                  }}
                />
                <div className="mx-auto">
                  <h3 className="h-20 text-primary text-center mt-5">
                    {school.name}
                  </h3>
                  <p className="text-14 text-grey-dark text-center">
                    {school.location}
                  </p>
                </div>
              </div>

              <div className="d-flex justify-between align-items-center mt-15">
                <RenderIf condition={studentInfo.photoUrl}>
                  <div
                    className="backgroundImage"
                    style={{
                      backgroundImage: `url(${studentInfo.photoUrl})`,
                    }}
                  />
                </RenderIf>

                <div>
                  <p className="text-14 text-grey-light">Student ID</p>
                  <h3 className="h-14">
                    {studentInfo.schoolStudentId
                      ? studentInfo.schoolStudentId
                      : studentInfo.studentId}
                  </h3>
                </div>
                <div>
                  <p className="text-14 text-grey-light">Student Name</p>
                  <h3 className="h-14">
                    {studentInfo.firstName} {studentInfo?.otherNames}{' '}
                    {studentInfo.lastName}
                  </h3>
                </div>

                <div>
                  <p className="text-14 text-grey-light">Class/Grade</p>
                  <h3 className="h-14">
                    {studentInfo.currentClassDescription}
                  </h3>
                </div>

                <div>
                  <p className="text-14 text-grey-light">Date Of Birth</p>
                  <h3 className="h-14">
                    {formatDate(studentInfo.dateOfBirth)}
                  </h3>
                </div>
              </div>

              <div className="text-16 text-primary font-bold text-center mt-30 mb-10">
                ACADEMIC TRANSCRIPT
              </div>
              <p className="text-14 text-grey-dark text-center mb-30">
                {coverage.firstAcademicYear && coverage.lastAcademicYear
                  ? coverage.firstAcademicYear === coverage.lastAcademicYear
                    ? `Records from ${coverage.firstAcademicYear}`
                    : `Records from ${coverage.firstAcademicYear} to ${coverage.lastAcademicYear}`
                  : 'Records from current academic year'}
              </p>

              <div className="transcript-records">
                {academicYears.map((academicRecord) => (
                  <div
                    key={academicRecord.academicYearId}
                    className="transcript-academic-year mb-30"
                  >
                    <div className="d-flex gap-50 align-items-center mb-15 font-bold">
                      <div>
                        <span>Academic Year: </span>
                        <span>{academicRecord.academicYear}</span>
                      </div>
                      <div>
                        <span>Class: </span>
                        <span>{academicRecord.classDescription}</span>
                      </div>
                    </div>

                    {academicRecord.terms?.map((termRecord) =>
                      renderTerm(termRecord),
                    )}

                    <div className="hr" />
                  </div>
                ))}
              </div>

              {renderGradingScales()}

              <div className="d-flex justify-between mt-40">
                <SignatureForm title="Headteacher/Principal" />
                <SignatureForm title="Director" />
              </div>
            </div>
          </div>

          <div className="inner-footer no-print">
            <div className="d-flex justify-between gap-20">
              <CustomButton
                classes="outline secondary font-bold"
                onClick={closeModal}
              >
                CLOSE
              </CustomButton>
              <CustomButton
                classes="primary font-bold"
                onClick={() => window.print()}
              >
                <Printer size="11" strokeWidth="5px" /> &nbsp; PRINT
              </CustomButton>
            </div>
          </div>
        </div>
      </Modal>
    </PrintView>
  );
}
